import React, { useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import type { StrategyRdMatch, StrategyRdOhlcBar } from "../../../types/strategyRd";
import type { DeploySignal, Deployment } from "../../../types/strategyDeploy";
import {
  strategyRdConceptDrawingsForMatches,
  strategyRdMergeLevelSegments,
  type StrategyRdConceptDrawing,
} from "../../../utils/strategyRdConceptDrawings";
import { fmtPrice } from "./liveStrategiesFormat";

/** Lab chart palette (grey candles, surf entry, red risk / green reward). */
const CANDLE_UP = "#8E97A6";
const CANDLE_DOWN = "#374151";
const SURF = "#38bbd1";
const ENTRY = "#7DD3E8";
const RISK_FILL = "rgba(224, 1, 1, 0.12)";
const REWARD_FILL = "rgba(6, 227, 99, 0.12)";
const GREEN = "#06e363";
const RED = "#fe4244";
const AXIS_W = 64;
const TIME_H = 20;

type Props = {
  dep: Deployment;
  candles: StrategyRdOhlcBar[];
  matches: StrategyRdMatch[];
  signals: DeploySignal[];
  height?: number;
  className?: string;
};

function ms(t: string | null | undefined): number | null {
  if (!t) return null;
  const v = new Date(t).getTime();
  return Number.isFinite(v) ? v : null;
}

/** Index of the last candle opening at or before ``t``. */
function indexAt(times: number[], t: number): number {
  let lo = 0;
  let hi = times.length - 1;
  if (t <= times[0]) return 0;
  if (t >= times[hi]) return hi;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (times[mid] <= t) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.round(e.contentRect.width)));
    ro.observe(el);
    setW(el.getBoundingClientRect().width);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

type RangeBox = { from: number; to: number; high: number; low: number; label: string; forming: boolean };

/** Minutes after local midnight in ``tz`` for an epoch ms. */
function minutesIn(tz: string, t: number): { day: string; m: number } {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date(t));
  const get = (k: string) => parts.find((p) => p.type === k)?.value ?? "0";
  return { day: `${get("year")}-${get("month")}-${get("day")}`, m: (Number(get("hour")) % 24) * 60 + Number(get("minute")) };
}

function hhmm(v: unknown): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(v ?? "").trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** Clock-time opening ranges (e.g. the Asian range) in the window — what a range strategy is
 *  waiting on, drawn even before any setup forms. */
function openingRanges(params: Record<string, unknown>, candles: StrategyRdOhlcBar[], times: number[]): RangeBox[] {
  const start = hhmm(params.range_start);
  const end = hhmm(params.range_end);
  if (start == null || end == null || end <= start) return [];
  const tz = String(params.range_tz || "UTC");
  const byDay = new Map<string, { idx: number[] }>();
  times.forEach((t, i) => {
    let local;
    try {
      local = minutesIn(tz, t);
    } catch {
      return;
    }
    if (local.m >= start && local.m < end) {
      const g = byDay.get(local.day) ?? { idx: [] };
      g.idx.push(i);
      byDay.set(local.day, g);
    }
  });
  const last = times.length ? minutesIn(tz, times[times.length - 1]) : null;
  return Array.from(byDay.entries()).map(([day, g]) => ({
    from: g.idx[0],
    to: g.idx[g.idx.length - 1],
    high: Math.max(...g.idx.map((i) => candles[i].high)),
    low: Math.min(...g.idx.map((i) => candles[i].low)),
    label: `Range ${String(params.range_start)}–${String(params.range_end)}`,
    forming: !!last && last.day === day && last.m < end,
  }));
}

/** The strategy as it sees the market: live candles, the setups it is watching (drawn exactly
 *  like the Lab draws them), and its trades with their stop / target boxes. */
export function LiveChart({ dep, candles, matches, signals, height = 300, className }: Props): JSX.Element {
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const ctx = (dep.strategy_snapshot?.scan_context ?? {}) as Record<string, unknown>;
  const setup = (ctx.setup ?? {}) as Record<string, unknown>;

  const drawings = useMemo((): StrategyRdConceptDrawing[] => {
    if (!candles.length || !matches.length) return [];
    try {
      const raw = strategyRdConceptDrawingsForMatches({
        matches,
        setupId: String(setup.id ?? ctx.setup_id ?? ctx.pattern_id ?? ""),
        confluences: (ctx.confluences as string[] | undefined) ?? [],
        candles,
        chartCandleType: dep.candle_type,
        setupCandleType: String(setup.candle_type ?? dep.candle_type),
      });
      return strategyRdMergeLevelSegments(raw).filter((d) => !d.paneId);
    } catch {
      return [];
    }
  }, [candles, matches, dep.candle_type, ctx, setup]);

  const times = useMemo(() => candles.map((c) => ms(c.timestamp) ?? 0), [candles]);
  const ranges = useMemo(
    () => openingRanges((setup.params ?? {}) as Record<string, unknown>, candles, times),
    [setup.params, candles, times]
  );
  const trades = useMemo(() => {
    if (!times.length) return [];
    const first = times[0];
    return signals.filter((s) => {
      const t = ms(s.entry_bar_time ?? s.created_at);
      return t != null && t >= first && ["open", "paper", "closed", "pending", "submitted"].includes(s.status);
    });
  }, [signals, times]);

  if (!candles.length) {
    return (
      <div
        className={cn("flex items-center justify-center rounded-[6px] bg-muted/30 text-xs text-muted-foreground", className)}
        style={{ height }}
      >
        The chart appears after the next candle check.
      </div>
    );
  }

  const plotW = Math.max(0, width - AXIS_W);
  const plotH = height - TIME_H;
  const future = Math.max(8, Math.round(candles.length * 0.08));
  const step = plotW / (candles.length + future);
  const x = (i: number) => step * i + step / 2;

  // Price range: candles, plus drawings / trades that sit near them (far-off levels would squash the view).
  let lo = Math.min(...candles.map((c) => c.low));
  let hi = Math.max(...candles.map((c) => c.high));
  const span0 = hi - lo || Math.abs(hi) * 0.01 || 1;
  const near = (p: number | null | undefined) => p != null && Number.isFinite(p) && p > lo - span0 && p < hi + span0;
  for (const d of drawings) for (const p of [d.high, d.low, d.price]) if (near(p)) { lo = Math.min(lo, p!); hi = Math.max(hi, p!); }
  for (const s of trades) for (const p of [s.stop_price, s.target_price]) if (near(p)) { lo = Math.min(lo, p!); hi = Math.max(hi, p!); }
  const pad = (hi - lo) * 0.06 || 1;
  lo -= pad;
  hi += pad;
  const y = (p: number) => ((hi - p) / (hi - lo)) * plotH;
  const ix = (t: string | null | undefined, fallback: number) => {
    const v = ms(t);
    return v == null ? fallback : indexAt(times, v);
  };
  const ticks = Array.from({ length: 5 }, (_, k) => lo + ((hi - lo) * (k + 0.5)) / 5);
  const nTicks = Math.max(2, Math.min(6, Math.floor(plotW / 90)));
  const timeTicks = Array.from({ length: nTicks }, (_, k) => Math.round(((candles.length - 1) * (k + 0.5)) / nTicks));
  const last = candles[candles.length - 1];
  const hc = hover != null ? candles[hover] : null;
  const bodyW = Math.max(1, step * 0.62);

  return (
    <div ref={wrapRef} className={cn("relative w-full select-none", className)} data-live-chart="">
      {width > 0 ? (
        <svg
          width={width}
          height={height}
          className="block"
          onMouseMove={(e) => {
            const r = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
            const i = Math.floor((e.clientX - r.left) / step);
            setHover(i >= 0 && i < candles.length ? i : null);
          }}
          onMouseLeave={() => setHover(null)}
          role="img"
          aria-label={`${dep.symbol} ${dep.candle_type} live chart`}
        >
          {ticks.map((p) => (
            <g key={p}>
              <line x1={0} x2={plotW} y1={y(p)} y2={y(p)} stroke="var(--border)" strokeDasharray="2 4" />
              <text x={plotW + 6} y={y(p) + 3} className="fill-muted-foreground text-[10px] tabular-nums">
                {fmtPrice(p)}
              </text>
            </g>
          ))}

          {/* Opening ranges: the box while it forms, then its high / low carried to the right. */}
          {ranges.map((r, k) => {
            const x0 = x(r.from) - step / 2;
            const x1 = x(r.to) + step / 2;
            const latest = k === ranges.length - 1;
            const xr = latest ? plotW : Math.min(plotW, x1 + step * 60);
            return (
              <g key={`rng-${r.from}`} data-live-range="">
                <rect x={x0} width={x1 - x0} y={y(r.high)} height={Math.max(1, y(r.low) - y(r.high))}
                  fill="color-mix(in oklab, #38bbd1 10%, transparent)" stroke={SURF} strokeOpacity={0.45} strokeDasharray={r.forming ? "3 3" : undefined} />
                {[r.high, r.low].map((p) => (
                  <line key={p} x1={x1} x2={xr} y1={y(p)} y2={y(p)} stroke={SURF} strokeOpacity={latest ? 0.9 : 0.35} strokeDasharray="5 4" />
                ))}
                {latest ? (
                  <>
                    <text x={x0 + 4} y={y(r.high) - 4} fontSize={10} fill={SURF}>
                      {r.forming ? `${r.label} · forming` : r.label}
                    </text>
                    <text x={xr - 4} y={y(r.high) - 4} fontSize={10} fill={SURF} textAnchor="end">High {fmtPrice(r.high)}</text>
                    <text x={xr - 4} y={y(r.low) + 12} fontSize={10} fill={SURF} textAnchor="end">Low {fmtPrice(r.low)}</text>
                  </>
                ) : null}
              </g>
            );
          })}

          {/* What the strategy is watching — the Lab's own drawings. */}
          {drawings.map((d) => {
            const a = ix(d.fromBarTime ?? d.barTime, 0);
            const b = d.toBarTime ? ix(d.toBarTime, candles.length - 1) : d.kind === "level" ? candles.length + future - 1 : a;
            const x0 = x(a) - step / 2;
            const x1 = Math.max(x(b) + step / 2, x0 + step);
            if (d.kind === "zone" && d.high != null && d.low != null) {
              return (
                <g key={d.id}>
                  <rect x={x0} width={x1 - x0} y={y(d.high)} height={Math.max(1, y(d.low) - y(d.high))}
                    fill="color-mix(in oklab, #38bbd1 12%, transparent)" stroke={SURF} strokeOpacity={0.5} />
                  {d.showLabel !== false ? <text x={x0 + 4} y={y(d.high) + 11} fontSize={10} fill={SURF}>{d.label}</text> : null}
                </g>
              );
            }
            if (d.kind === "level" && d.price != null && near(d.price)) {
              return (
                <g key={d.id}>
                  <line x1={x0} x2={x1} y1={y(d.price)} y2={y(d.price)} stroke={SURF} strokeWidth={1.25} strokeDasharray="5 4" />
                  {d.showLabel !== false ? (
                    <text x={Math.min(x1, plotW) - 4} y={y(d.price) - 4} fontSize={10} fill={SURF} textAnchor="end">{d.label}</text>
                  ) : null}
                </g>
              );
            }
            if (d.kind === "bar") {
              return <rect key={d.id} x={x0} width={x1 - x0} y={0} height={plotH} fill="color-mix(in oklab, #38bbd1 8%, transparent)" />;
            }
            if (d.kind === "ray" && (d.fromPrice ?? d.price) != null) {
              const p0 = (d.fromPrice ?? d.price)!;
              const p1 = (d.toPrice ?? p0)!;
              return <line key={d.id} x1={x(a)} x2={x(b)} y1={y(p0)} y2={y(p1)} stroke={SURF} strokeWidth={1.25} />;
            }
            if (d.kind === "marker" && d.price != null && near(d.price)) {
              const up = d.markerStyle !== "arrowdown";
              const cy = y(d.price);
              return (
                <path key={d.id} d={up ? `M${x(a)} ${cy - 5} l4 7 h-8z` : `M${x(a)} ${cy + 5} l4 -7 h-8z`}
                  fill={up ? GREEN : RED} />
              );
            }
            return null;
          })}

          {/* Trades: risk / reward boxes from entry to exit (or into the future while open). */}
          {trades.map((s) => {
            const e = s.fill_price ?? s.entry_price;
            if (e == null) return null;
            const a = ix(s.entry_bar_time ?? s.created_at, candles.length - 1);
            const open = !s.exit_time;
            const b = open ? candles.length + future - 1 : ix(s.exit_time, candles.length - 1);
            const x0 = x(a) - step / 2;
            const x1 = x(b) + step / 2;
            return (
              <g key={s.id}>
                {s.stop_price != null ? (
                  <rect x={x0} width={x1 - x0} y={Math.min(y(e), y(s.stop_price))} height={Math.abs(y(e) - y(s.stop_price))} fill={RISK_FILL} />
                ) : null}
                {s.target_price != null ? (
                  <rect x={x0} width={x1 - x0} y={Math.min(y(e), y(s.target_price))} height={Math.abs(y(e) - y(s.target_price))} fill={REWARD_FILL} />
                ) : null}
                <line x1={x0} x2={x1} y1={y(e)} y2={y(e)} stroke={ENTRY} strokeWidth={1.5} />
              </g>
            );
          })}

          {candles.map((c, i) => {
            const up = c.close >= c.open;
            const col = up ? CANDLE_UP : CANDLE_DOWN;
            const top = y(Math.max(c.open, c.close));
            const bot = y(Math.min(c.open, c.close));
            return (
              <g key={c.timestamp}>
                <line x1={x(i)} x2={x(i)} y1={y(c.high)} y2={y(c.low)} stroke={col} />
                <rect x={x(i) - bodyW / 2} width={bodyW} y={top} height={Math.max(1, bot - top)} fill={col} />
              </g>
            );
          })}

          {/* Entry / exit markers on top of the candles. */}
          {trades.map((s) => {
            const e = s.fill_price ?? s.entry_price;
            if (e == null) return null;
            const a = ix(s.entry_bar_time ?? s.created_at, candles.length - 1);
            const long = s.direction === "long";
            const cy = y(e);
            return (
              <g key={`m-${s.id}`}>
                <path d={long ? `M${x(a)} ${cy + 4} l5 9 h-10z` : `M${x(a)} ${cy - 4} l5 -9 h-10z`} fill={long ? GREEN : RED} />
                {s.exit_time && s.exit_price != null ? (
                  <circle cx={x(ix(s.exit_time, candles.length - 1))} cy={y(s.exit_price)} r={3.5}
                    fill="var(--background)" stroke={(s.r_multiple ?? 0) >= 0 ? GREEN : RED} strokeWidth={2} />
                ) : null}
              </g>
            );
          })}

          {/* Last price. */}
          <line x1={0} x2={plotW} y1={y(last.close)} y2={y(last.close)} stroke={SURF} strokeOpacity={0.6} strokeDasharray="1 3" />
          <rect x={plotW + 2} y={y(last.close) - 9} width={AXIS_W - 4} height={18} rx={4} fill={SURF} />
          <text x={plotW + AXIS_W / 2} y={y(last.close) + 4} textAnchor="middle" fontSize={10.5} fill="#fff" className="tabular-nums">
            {fmtPrice(last.close)}
          </text>

          {timeTicks.map((i) => (
            <text key={i} x={x(i)} y={height - 5} textAnchor="middle" className="fill-muted-foreground text-[10px]">
              {new Date(candles[i].timestamp).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}
            </text>
          ))}

          {hover != null ? <line x1={x(hover)} x2={x(hover)} y1={0} y2={plotH} stroke="var(--muted-foreground)" strokeDasharray="2 3" /> : null}
        </svg>
      ) : (
        <div style={{ height }} />
      )}
      {hc ? (
        <div className="pointer-events-none absolute left-2 top-2 rounded-[6px] border border-solid border-border bg-popover/95 px-2 py-1 font-ibm-plex text-[11px] tabular-nums shadow-sm">
          {new Date(hc.timestamp).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
          {"  "}O {fmtPrice(hc.open)} H {fmtPrice(hc.high)} L {fmtPrice(hc.low)} C {fmtPrice(hc.close)}
        </div>
      ) : null}
    </div>
  );
}

/** A small close-price line for the strategy cards. */
export function MiniChart({ candles, height = 44 }: { candles: StrategyRdOhlcBar[]; height?: number }): JSX.Element {
  const [ref, width] = useWidth<HTMLDivElement>();
  const pts = candles.slice(-72);
  if (pts.length < 2) return <div ref={ref} style={{ height }} />;
  const lo = Math.min(...pts.map((c) => c.close));
  const hi = Math.max(...pts.map((c) => c.close));
  const sx = (i: number) => (i / (pts.length - 1)) * width;
  const sy = (p: number) => 2 + (hi === lo ? 0.5 : (hi - p) / (hi - lo)) * (height - 4);
  const line = pts.map((c, i) => `${i ? "L" : "M"}${sx(i).toFixed(1)} ${sy(c.close).toFixed(1)}`).join(" ");
  // Market price in the brand blue: green / red would read as profit or loss.
  const col = SURF;
  return (
    <div ref={ref} className="w-full" data-live-mini-chart="">
      {width > 0 ? (
        <svg width={width} height={height} className="block" aria-hidden>
          <defs>
            <linearGradient id="mini-price" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={col} stopOpacity={0.22} />
              <stop offset="100%" stopColor={col} stopOpacity={0} />
            </linearGradient>
          </defs>
          <path d={`${line} L${width} ${height} L0 ${height}Z`} fill="url(#mini-price)" />
          <path d={line} fill="none" stroke={col} strokeWidth={1.5} />
          <circle cx={sx(pts.length - 1)} cy={sy(pts[pts.length - 1].close)} r={2.5} fill={col} />
        </svg>
      ) : null}
    </div>
  );
}

/** A strategy's running total of R, one point per closed trade (starts at 0). Green above zero,
 *  red below — this one is profit and loss. */
export function MiniResultChart({ rPath, height = 44 }: { rPath: number[]; height?: number }): JSX.Element {
  const [ref, width] = useWidth<HTMLDivElement>();
  const cum = [0];
  for (const r of rPath) cum.push(cum[cum.length - 1] + r);
  const lo = Math.min(0, ...cum);
  const hi = Math.max(0, ...cum);
  const sx = (i: number) => (i / Math.max(1, cum.length - 1)) * width;
  const sy = (v: number) => 2 + (hi === lo ? 0.5 : (hi - v) / (hi - lo)) * (height - 4);
  const line = cum.map((v, i) => `${i ? "L" : "M"}${sx(i).toFixed(1)} ${sy(v).toFixed(1)}`).join(" ");
  const last = cum[cum.length - 1];
  const col = last >= 0 ? GREEN : RED;
  return (
    <div ref={ref} className="w-full" data-live-mini-result="">
      {width > 0 ? (
        <svg width={width} height={height} className="block" aria-hidden>
          <line x1={0} x2={width} y1={sy(0)} y2={sy(0)} stroke="var(--border)" strokeDasharray="3 3" />
          <path d={line} fill="none" stroke={col} strokeWidth={1.5} />
          {cum.map((v, i) => (i ? <circle key={i} cx={sx(i)} cy={sy(v)} r={1.8} fill={col} /> : null))}
        </svg>
      ) : null}
    </div>
  );
}
